import { ButtonCus } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/common/Form';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { formatCurrency } from '@/utils/formatNumber';
import { formatDateTime } from '@/utils/datetime';
import { DISPPLAY_MOMENT_YMD_HM } from '@/configs/constants';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface CancelAdditionalPaymentProps {
    paymentId: number,
    createdDate?: string,
    amount?: string | number,
    reason?: string,
    reload?: Function
}
export default observer(function CancelAdditionalPayment({ paymentId, createdDate, amount, reason, reload }: CancelAdditionalPaymentProps) {
    // hook
    const { t } = useTranslation();
    const { t: tField } = useTranslation('transation', { keyPrefix: 'field' });

    // store
    const { reservationStore: { cancelAdditionalPayment }, modalStore: { hideAllModals, showAlertModal } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        isNotSendOwner: yup.boolean()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onSubmit',
        defaultValues: {
            isNotSendOwner: false
        }
    });

    // function
    const onSubmit = async (data: FormData) => {
        const result = await flowResult(cancelAdditionalPayment({ paymentId, ...data }));
        if (result) {
            hideAllModals();
            showAlertModal({
                id: 'cancel-additional-payment-success',
                type: 'success',
                content: t('messages.cancel_additional_payment_success'),
                onClickXIcon: () => {
                    reload && reload();
                }
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div className='bg-background-10 rounded-md p-3 flex flex-col gap-1'>
                    <div className='flex gap-2'>
                        <span className='font-semibold shrink-0'>{`${tField('date')}:`}</span>
                        <span>{createdDate ? formatDateTime(createdDate, DISPPLAY_MOMENT_YMD_HM, { useLocalTime: false }) : ''}</span>
                    </div>
                    <div className='flex gap-2'>
                        <span className='font-semibold shrink-0'>{`${tField('reason')}:`}</span>
                        <span className='whitespace-pre-line break-all'>{reason}</span>
                    </div>
                    <div className='flex gap-2'>
                        <span className='font-semibold shrink-0'>{`${tField('amount2')}:`}</span>
                        <span>{`${t('currency.currency_symbol')}${formatCurrency(amount)}`}</span>
                    </div>
                </div>
                <p className='text-center whitespace-pre-line mt-4'>
                    {t('messages.confirm_cancel_additional_payment')}
                </p>
                <div className='mt-4'>
                    <FormField
                        control={form.control}
                        name='isNotSendOwner'
                        render={({ field }) => (
                            <FormItem className='flex items-center justify-center'>
                                <FormControl>
                                    <Checkbox
                                        checked={field?.value}
                                        onCheckedChange={field.onChange}
                                    />
                                </FormControl>
                                <FormLabel className='font-normal cursor-pointer mb-0 ml-1.5'>
                                    {t('sentences.not_send_mail_to_owner')}
                                </FormLabel>
                            </FormItem>
                        )}
                    />
                </div>
                <div>
                    <div className='flex justify-center mt-8'>
                        <ButtonCus variant='default' size='sm' disabled={form.formState.isSubmitting} type='submit'>{t('button.confirm')}</ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
